import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';

export const blue = '#0d4efe';
export const teal = '#01ebb2';
const ink = '#1a1a2e';
const muted = '#5a6478';

export const StyledDialog = styled(Dialog)(() => ({
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(26, 26, 46, 0.52)',
    backdropFilter: 'blur(4px)',
  },
  '& .MuiDialog-paper': {
    borderRadius: 16,
    overflow: 'hidden',
    border: '1px solid rgba(13, 78, 254, 0.14)',
    boxShadow:
      '0 24px 56px rgba(13, 78, 254, 0.16), 0 8px 24px rgba(0, 0, 0, 0.06)',
    background: '#ffffff',
    maxWidth: 480,
    width: '100%',
    margin: 16,
  },
}));

export const DialogHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: theme.spacing(1.5),
  padding: theme.spacing(2.5, 3),
  paddingRight: theme.spacing(6),
  position: 'relative',
  background:
    'linear-gradient(135deg, rgba(1, 235, 178, 0.14) 0%, rgba(13, 78, 254, 0.1) 55%, rgba(13, 78, 254, 0.04) 100%)',
  borderBottom: '1px solid rgba(13, 78, 254, 0.1)',
}));

export const HeaderMain = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 14,
  minWidth: 0,
});

export const HeaderIconWrap = styled('div')({
  flexShrink: 0,
  width: 44,
  height: 44,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background:
    'linear-gradient(145deg, rgba(1, 235, 178, 0.2) 0%, rgba(13, 78, 254, 0.15) 100%)',
  border: '1px solid rgba(13, 78, 254, 0.18)',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.6)',
  color: blue,
});

export const HeaderTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '1.125rem',
  lineHeight: 1.35,
  letterSpacing: '-0.02em',
  color: ink,
});

export const HeaderSubtitle = styled(Typography)({
  marginTop: 4,
  fontSize: '0.875rem',
  lineHeight: 1.45,
  color: muted,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

export const DialogCloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
  color: muted,
  borderRadius: 10,
  '&:hover': {
    background: 'rgba(13, 78, 254, 0.08)',
    color: ink,
  },
}));

export const DialogBody = styled('div')(({ theme }) => ({
  padding: theme.spacing(2, 3, 1),
  maxHeight: 'min(68vh, 560px)',
  overflowY: 'auto',
}));

export const FormSection = styled('section')({
  marginBottom: 16,
  padding: '14px 16px',
  borderRadius: 12,
  background: '#f7fafc',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  '&:last-of-type': {
    marginBottom: 0,
  },
});

export const SectionLabel = styled(Typography)({
  display: 'block',
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#8a94a6',
  marginBottom: 10,
});

export const UrgencySection = styled(FormSection)({
  paddingBottom: 12,
});

export const UrgencyOptionsList = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
});

export const UrgencyOptionButton = styled('button')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1.5px solid
    ${({ $selected, $urgent }) =>
      $selected
        ? $urgent
          ? 'rgba(220, 38, 38, 0.4)'
          : 'rgba(13, 78, 254, 0.4)'
        : 'rgba(0, 0, 0, 0.1)'};
  background: ${({ $selected, $urgent }) =>
    $selected
      ? $urgent
        ? 'rgba(220, 38, 38, 0.08)'
        : 'rgba(13, 78, 254, 0.08)'
      : '#fff'};
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background 0.15s ease,
    box-shadow 0.15s ease;
  box-shadow: ${({ $selected }) =>
    $selected ? '0 2px 8px rgba(13, 78, 254, 0.12)' : 'none'};

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    border-color: ${({ $urgent }) =>
      $urgent ? 'rgba(220, 38, 38, 0.35)' : 'rgba(13, 78, 254, 0.3)'};
    background: ${({ $urgent }) =>
      $urgent ? 'rgba(220, 38, 38, 0.05)' : 'rgba(13, 78, 254, 0.05)'};
  }

  &:focus-visible {
    outline: 2px solid ${blue};
    outline-offset: 2px;
  }

  .option-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ $selected, $urgent }) =>
      $selected ? ($urgent ? '#dc2626' : blue) : muted};
  }

  .option-label {
    font-size: 0.8125rem;
    font-weight: ${({ $selected }) => ($selected ? 600 : 500)};
    color: ${ink};
    line-height: 1.2;
    white-space: nowrap;
  }
`;

export const LocationSection = styled(FormSection)({});

export const CityStateRow = styled('div')`
  display: flex;
  gap: 10px;
  align-items: flex-start;

  & > *:first-child {
    flex: 2;
  }

  & > *:last-child {
    flex: 1;
    min-width: 88px;
  }

  @media (max-width: 400px) {
    flex-direction: column;

    & > *:first-child,
    & > *:last-child {
      flex: 1;
      width: 100%;
    }
  }
`;

export const LocationHint = styled('p')({
  margin: '10px 0 0',
  fontSize: '0.75rem',
  lineHeight: 1.45,
  color: '#8a94a6',
});

export const DetailsSection = styled(FormSection)({});

export const DetailsField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: 10,
    backgroundColor: '#fff',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(0, 0, 0, 0.1)',
    borderWidth: '1.5px',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(13, 78, 254, 0.35)',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: blue,
    borderWidth: '1.5px',
  },
  '& .MuiOutlinedInput-root.Mui-focused': {
    boxShadow: '0 0 0 3px rgba(13, 78, 254, 0.08)',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: blue,
  },
});

export const PhotosSection = styled(FormSection)({
  paddingBottom: 12,
});

export const PhotoRow = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 10,
});

export const PhotoThumb = styled('div')({
  position: 'relative',
  width: 64,
  height: 64,
  borderRadius: 10,
  overflow: 'hidden',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06)',

  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },

  '& button': {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 20,
    height: 20,
    border: 'none',
    borderRadius: '50%',
    background: 'rgba(0, 0, 0, 0.6)',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '0.85rem',
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export const AddPhotoButton = styled('button')({
  width: 64,
  height: 64,
  borderRadius: 10,
  border: '1px dashed rgba(13, 78, 254, 0.35)',
  background: '#fff',
  color: blue,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
  fontSize: '0.65rem',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'border-color 0.15s ease, background 0.15s ease',
  '&:hover': {
    borderColor: blue,
    background: 'rgba(13, 78, 254, 0.04)',
  },
});

export const FormErrorAlert = styled(Alert)({
  marginTop: 12,
  borderRadius: 10,
  fontSize: '0.875rem',
});

export const DialogFooter = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
  gap: theme.spacing(1.25),
  padding: theme.spacing(2, 3, 2.75),
  background: 'linear-gradient(180deg, rgba(247, 250, 252, 0.9) 0%, #f7fafc 100%)',
  borderTop: '1px solid rgba(0, 0, 0, 0.06)',
}));

export const PrimaryButton = styled(Button)(() => ({
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 10,
  padding: '10px 20px',
  color: '#fff',
  background: `linear-gradient(90deg, ${teal} 0%, ${blue} 100%)`,
  boxShadow: '0 4px 14px rgba(13, 78, 254, 0.35)',
  gap: 6,
  '&:hover': {
    background: `linear-gradient(90deg, ${teal} 0%, ${blue} 100%)`,
    opacity: 0.95,
    boxShadow: '0 6px 18px rgba(13, 78, 254, 0.4)',
  },
  '&.Mui-disabled': {
    color: 'rgba(255,255,255,0.85)',
    background: 'linear-gradient(90deg, rgba(1,235,178,0.5) 0%, rgba(13,78,254,0.5) 100%)',
  },
}));

export const SecondaryButton = styled(Button)(() => ({
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 10,
  padding: '10px 20px',
  color: ink,
  borderColor: 'rgba(13, 78, 254, 0.28)',
  backgroundColor: '#fff',
  '&:hover': {
    borderColor: blue,
    backgroundColor: 'rgba(13, 78, 254, 0.06)',
  },
}));

/** @deprecated use SectionLabel — kept for imports that used UrgencyLabel */
export const UrgencyLabel = SectionLabel;
